import { onCleanup, createEffect, onMount } from "solid-js";
import {
  customShadowlessElement,
  CorrectComponentType,
  correctElementType,
  toHyphenated,
} from "@brytdesigns/web-component-core/utils";

const DATA_VALUE_ATTR = toHyphenated("dataValue");

type Props = {
  dataValue: number;
};

const QuantityInput: CorrectComponentType<Props> = (props, { element }) => {
  const input = element.querySelector("input");
  const controller = new AbortController();

  element.querySelectorAll("button").forEach((button) => {
    button.addEventListener("click", onButtonClick, {
      signal: controller.signal,
    });
  });

  createEffect(() => {
    validateQtyRules();
    if (props.dataValue !== input?.valueAsNumber) return;
    element.setAttribute(DATA_VALUE_ATTR, input.value);
    input.value = `${props.dataValue}`;
  });

  function constrainedInputValue() {
    if (input?.value === undefined) return -Infinity;
    const value = parseInt(input.value);

    let min = -Infinity,
      max = Infinity;

    if (input.min) min = parseInt(input.min);
    if (input.max) max = parseInt(input.max);

    return Math.min(Math.max(value, min), max);
  }

  function validateQtyRules() {
    if (input?.value === undefined) return;
    const value = parseInt(input.value);
    if (input.min) {
      const min = parseInt(input.min);
      const buttonMinus = element.querySelector("button[name='decrement']");
      buttonMinus?.toggleAttribute("disabled", value <= min);
    }
    if (input.max) {
      const max = parseInt(input.max);
      const buttonPlus = element.querySelector("button[name='increment']");
      buttonPlus?.toggleAttribute("disabled", value >= max);
    }
  }

  function onButtonClick(event: Event) {
    event.preventDefault();
    if (input?.value === undefined) return;
    const previousValue = input.value;

    let target: HTMLButtonElement | null = event.target as HTMLButtonElement;

    if (target.tagName !== "button")
      target = target.closest<HTMLButtonElement>("button");
    if (!target) return;

    target.name === "increment" ? input.stepUp() : input.stepDown();
    if (previousValue !== input.value) {
      input.dispatchEvent(
        new Event("change", { bubbles: true, cancelable: true }),
      );
    }
  }

  onMount(() => {
    validateQtyRules();

    function onInputChange() {
      if (input?.value === undefined) return;
      input.value = `${constrainedInputValue()}`;
      element.setAttribute(DATA_VALUE_ATTR, input.value);
      validateQtyRules();
    }

    input?.addEventListener("change", onInputChange);

    onCleanup(() => {
      input?.removeEventListener("change", onInputChange);
    });
  });

  onCleanup(() => {
    controller.abort();
  });
};

customShadowlessElement(
  "quantity-input",
  { dataValue: 1 },
  correctElementType(QuantityInput),
);
